import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, portfolioItems, testimonials } from '../mock';
import * as LucideIcons from 'lucide-react';

const Homepage = () => {
  return (
    <div className="min-h-screen page-transition">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNGgtMnYyaDJ2LTJ6bS00IDB2Mmg0di0yaC00em0tMiAwaC0ydjJoMnYtMnptLTItNHYyaDJ2LTJoLTJ6bTIgMHYtMmgtMnYyaDJ6bTAtNHYyaDJ2LTJoLTJ6bTItMmgydi0yaC0ydjJ6bTAgMGgydi0yaC0ydjJ6bTIgMnYtMmgydjJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
        </div>

        {/* Mobile Background Image - Behind text with reduced opacity */}
        <div className="absolute inset-0 lg:hidden z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_european-touch-paint/artifacts/atvp6r4l_pexels-georgetan-22800058.jpg"
            alt="Professional painting services"
            className="w-full h-full object-cover opacity-25"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left animate-in fade-in slide-in-from-bottom-8 duration-1000 order-2 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 mb-8 shadow-sm">
                <Sparkles className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-gray-700">23+ Years of Excellence</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                {/* Mobile: Each word on own line */}
                <span className="lg:hidden">
                  <span className="block">Precision</span>
                  <span className="block text-gray-900">Painting</span>
                  <span className="block">Elevated</span>
                  <span className="block text-gray-900">Spaces</span>
                </span>
                {/* Desktop: Precision Painting on one line, then Elevated Spaces */}
                <span className="hidden lg:inline">
                  <span className="whitespace-nowrap">Precision Painting</span><br />Elevated Spaces
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed">
                Residential & Commercial High-End Finishes
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-12">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300">
                    Get a Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button size="lg" variant="outline" className="border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-white px-8 py-6 text-lg transition-all duration-300">
                    View Portfolio
                  </Button>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-8 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-red-600" />
                  <span>Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-red-600" />
                  <span>Licensed Professional</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-red-600" />
                  <span>700+ Projects</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-red-600 fill-red-600" />
                  <span>4.98 Rating</span>
                </div>
              </div>
            </div>

            {/* Right Column - Hero Image (Desktop Only) */}
            <div className="relative hidden lg:block order-1 lg:order-2 lg:ml-16">
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_european-touch-paint/artifacts/fejzy6de_Gemini_Generated_Image_fa7hc2fa7hc2fa7h.png"
                    alt="Professional painting services"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                {/* Decorative Elements */}
                <div className="absolute -top-8 -right-8 w-64 h-64 bg-gold-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
                <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive painting solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              // Define object positioning for better cropping
              let objectPosition = 'object-center';
              if (service.id === 1) {
                // Interior Painting: Show chair and wall - position bottom-right
                objectPosition = 'object-[center_70%]';
              } else if (service.id === 3) {
                // Custom Finishes: Show paint roller and hand - position to show more roller
                objectPosition = 'object-[50%_40%]';
              }
              
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-gold-300 overflow-hidden">
                  <CardContent className="p-0">
                    {/* Service Image */}
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${objectPosition}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 w-12 h-12 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                        {IconComponent && <IconComponent className="w-6 h-6 text-red-600" />}
                      </div>
                    </div>
                    
                    {/* Service Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-all duration-300">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-4">
              Recent Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See the transformation we bring to every space
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.slice(0, 6).map((item) => (
              <div
                key={item.id}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img
                    src={item.after}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button variant="outline" size="lg" className="border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-all duration-300">
                View Full Portfolio
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 4).map((testimonial) => (
              <Card key={testimonial.id} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-gold-600 fill-gold-600" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {testimonials.slice(4, 6).map((testimonial) => (
              <Card key={testimonial.id} className="border-gray-200 hover:shadow-lg transition-shadow duration-300 hidden lg:block">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-gold-600 fill-gold-600" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAgNGgtMnYyaDJ2LTJ6bS00IDB2Mmg0di0yaC00em0tMiAwaC0ydjJoMnYtMnptLTItNHYyaDJ2LTJoLTJ6bTIgMHYtMmgtMnYyaDJ6bTAtNHYyaDJ2LTJoLTJ6bTItMmgydi0yaC0ydjJ6bTAgMGgydi0yaC0ydjJ6bTIgMnYtMmgydjJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-serif font-bold mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Get a free estimate today. We respond within 24 hours.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-10 py-6 text-lg shadow-2xl hover:shadow-red-500/50 transition-all duration-300">
              Book Your Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="mt-6 text-gray-400 text-sm">
            No obligation • Professional consultation • Fast response
          </p>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
